import { api, type StockLot, type StoresOverview } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { editLot, inspectLot, printLabels, receiveLot, reserveFromAircraft, showLot } from '../dialogs/stock';
import { openStockCheck } from './stock-check';
import { KIND_LABEL, LOCATION_KIND_LABEL, TAG_STYLE, expiryText, lotState } from '../stock-ui';
import { errorCard, esc, fmtDate, html, loading, pill, tile, toast } from '../ui';

/**
 * The store.
 *
 * Ordered the way the shelf is worked rather than the way the table is
 * indexed. What arrived and has not been looked at yet comes first, because a
 * lot sitting uninspected in quarantine is the one thing here that stops work;
 * then what has gone out of date, which stops it just as firmly but quietly.
 *
 * Nothing on this screen decides whether something has expired — the API does,
 * from the dates, on every read. A shelf that looked in date because a nightly
 * job failed is the exact failure a store exists to prevent, so there is no
 * stored flag anywhere to go stale.
 */

const FILTERS = [
  ['all', 'Everything'],
  ['QUARANTINE', 'Awaiting inspection'],
  ['SERVICEABLE', 'Serviceable'],
  ['RESERVED', 'Reserved for refit'],
  ['expiring', 'Expiring'],
  ['UNSERVICEABLE', 'Unserviceable'],
] as const;

function matches(l: StockLot, filter: string): boolean {
  if (filter === 'all') return !['DEPLETED', 'INSTALLED', 'RETURNED_TO_SUPPLIER'].includes(l.state);
  if (filter === 'expiring') return l.expired || (l.daysToExpiry !== null && l.daysToExpiry <= 90);
  return l.state === filter;
}

/**
 * The search runs here rather than on the server.
 *
 * The whole shelf is already loaded, so a round trip buys nothing and
 * costs a repaint of the entire page on every keystroke — tiles, panels,
 * scroll position and the caret with them. Typing should filter a table,
 * not reload a screen.
 */
function searched(l: StockLot, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [l.partNumber, l.description, l.batchLot, l.serialNumber, l.location?.code]
    .some((v) => (v ?? '').toLowerCase().includes(needle));
}

function view(o: StoresOverview, filter: string, picked: Set<string>, search: string): string {
  const shown = o.lots.filter((l) => matches(l, filter) && searched(l, search));
  const expiring = o.alerts.expired.length + o.alerts.expiringSoon.length;

  return html`
    <div class="page-h">
      <div>
        <h1>Stores</h1>
        <p class="dim">
          Receipt, incoming inspection, segregation and issue — 145.A.42 and 145.A.25(d)
        </p>
      </div>
      <div style="display:flex;gap:8px">
        <button class="btn" data-check>${o.openCheck ? 'Carry on counting' : 'Start a stock check'}</button>
        <button class="btn" data-reserve>Part off an aircraft</button>
        <button class="btn" data-import>Receive from paperwork</button>
        <button class="btn pri" data-receive>Receive stock</button>
      </div>
    </div>

    <div class="tiles">
      ${tile({
        label: 'Awaiting inspection',
        value: o.alerts.awaitingInspection.length,
        sub: o.alerts.awaitingInspection.length ? 'cannot be fitted yet' : 'nothing in quarantine',
        tone: o.alerts.awaitingInspection.length ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Expired',
        value: o.alerts.expired.length,
        sub: o.alerts.expired.length ? 'must not be fitted' : 'nothing out of date',
        tone: o.alerts.expired.length ? 'exceed' : 'ok',
      })}
      ${tile({
        label: 'Expiring within 90 days',
        value: o.alerts.expiringSoon.length,
        sub: 'order before it runs out',
        tone: o.alerts.expiringSoon.length ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Serviceable',
        value: o.counts.serviceable,
        sub: `${o.counts.reserved} reserved · ${o.counts.unserviceable} unserviceable`,
      })}
    </div>

    ${o.alerts.missingPaperwork.length
      ? callout('caution', '!', html`
          <b>${o.alerts.missingPaperwork.length} lot(s) have no acceptable release document.</b>
          145.A.42 asks for a Form 1 or an 8130-3 on a component, and a statement of conformity to
          specification naming both the manufacturing and the supplier source on material. None of
          them can pass incoming inspection or be issued until that is on file.
        `)
      : ''}

    ${o.openCheck
      ? callout('info', 'i', html`
          <b>${esc(o.openCheck.reference)} is open</b> — started ${esc(fmtDate(o.openCheck.startedAt))}.
          <a href="/stores/checks/${esc(o.openCheck.id)}">Carry on counting</a>.
        `)
      : ''}

    <div class="card">
      <div class="card-h">
        <div>
          <h2>On the shelf</h2>
          <p class="tiny dim">
            <span data-shown-count>${shown.length} of ${o.counts.total} shown</span>${expiring ? ` · ${expiring} expiring or expired` : ''}
          </p>
        </div>
        <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">
          <input class="in sm" data-search value="${esc(search)}" placeholder="part no., batch, serial, bin…" style="width:190px">
          <button class="btn sm" data-labels>
            Print labels${picked.size ? ` (${picked.size})` : ''}
          </button>
        </div>
      </div>

      <div class="tabs">
        ${FILTERS.map(
          ([key, label]) => html`
            <button class="tab ${filter === key ? 'on' : ''}" data-filter="${esc(key)}">${esc(label)}</button>
          `,
        ).join('')}
      </div>

      <div class="tblwrap">
        <table class="tbl">
          <thead>
            <tr>
              <th style="width:28px"></th>
              <th>Part number</th><th class="wide">Description</th>
              <th>Batch / serial</th><th class="col-opt">Qty</th>
              <th>Condition</th><th>Shelf life</th><th class="col-opt">Location</th><th></th>
            </tr>
          </thead>
          <tbody data-lot-rows>
            ${
              shown.length
                ? shown.map((l) => lotRow(l, picked)).join('')
                : '<tr><td colspan="9" class="muted">Nothing on this filter.</td></tr>'
            }
          </tbody>
        </table>
      </div>
      <div class="card-f">
        Everything received lands in quarantine and stays there until somebody inspects it — that one
        rule is most of 145.A.42. A lot with no acceptable paperwork, one that has expired, and one
        tagged to another aircraft are all refused at the point of issue rather than flagged afterwards.
      </div>
    </div>

    ${locationsCard(o)}
  `;
}

function lotRow(l: StockLot, picked: Set<string>): string {
  const s = lotState(l);
  const stripe = s === 'OVERDUE' ? 'exceed' : s === 'DUE_SOON' ? 'caution' : s === 'OK' ? 'ok' : 'na';

  return html`
    <tr>
      <td>
        <input type="checkbox" data-pick="${esc(l.id)}" ${picked.has(l.id) ? 'checked' : ''}
               aria-label="Select for labelling">
      </td>
      <td class="st s-${stripe} n">
        <a href="#" data-lot="${esc(l.id)}" class="reg">${esc(l.partNumber)}</a>
      </td>
      <td class="wide">
        ${esc(l.description)}
        <span class="sub2">${esc(KIND_LABEL[l.kind] ?? l.kind)}${
          l.releaseLabel ? ` · ${esc(l.releaseLabel)}${l.releaseReference ? ` ${esc(l.releaseReference)}` : ''}` : ''
        }</span>
        ${l.paperworkGap ? `<span class="sub2" style="color:var(--caution)">${esc(l.paperworkGap)}</span>` : ''}
        ${l.sourceAircraft
          ? `<span class="sub2" style="color:var(--caution)">off ${esc(l.sourceAircraft.registration)} — refit to that aircraft only</span>`
          : ''}
      </td>
      <td class="n tiny">
        ${esc(l.batchLot ?? '—')}${l.serialNumber ? `<span class="sub2">S/N ${esc(l.serialNumber)}</span>` : ''}
      </td>
      <td class="col-opt n">${esc(String(l.quantity))} ${esc(l.unit)}</td>
      <td style="white-space:nowrap">
        ${l.tag
          ? `<span class="chip" style="${TAG_STYLE[l.tag]}">${esc(l.stateLabel)}</span>`
          : `<span class="chip">${esc(l.stateLabel)}</span>`}
      </td>
      <td>
        ${l.effectiveExpiry
          ? pill(l.expired ? 'OVERDUE' : l.daysToExpiry !== null && l.daysToExpiry <= 90 ? 'DUE_SOON' : 'OK', expiryText(l))
          : '<span class="tiny muted">no shelf life</span>'}
        ${l.openedAt ? `<span class="sub2">opened ${esc(fmtDate(l.openedAt))}</span>` : ''}
      </td>
      <td class="col-opt tiny muted">${l.location ? esc(l.location.code) : '<span style="color:var(--caution)">nowhere</span>'}</td>
      <td>
        <div class="acts">
          ${l.state === 'QUARANTINE'
            ? `<button class="btn sm pri" data-inspect="${esc(l.id)}">Inspect</button>`
            : ''}
          <button class="btn sm" data-lot-open="${esc(l.id)}">Open</button>
          <button class="btn sm" data-lot-edit="${esc(l.id)}">Edit</button>
        </div>
      </td>
    </tr>
  `;
}

function locationsCard(o: StoresOverview): string {
  const byKind = (kind: string) => o.locations.filter((l) => l.kind === kind);

  return html`
    <details class="card" data-locations>
      <summary class="card-h">
        <div class="sum">
          <span class="twist" aria-hidden="true"></span>
          <div>
            <h2>Locations</h2>
            <p class="tiny dim">
              ${o.locations.length} bins — serviceable material kept apart from unserviceable, 145.A.25(d)
            </p>
          </div>
        </div>
        <button class="btn sm" data-add-location>Add a location</button>
      </summary>
      ${o.locations.length
        ? html`
            <div class="cols2">
              ${['SERVICEABLE', 'QUARANTINE', 'UNSERVICEABLE', 'SCRAP']
                .map((kind) => {
                  const list = byKind(kind);
                  return html`
                    <div class="rows">
                      <div class="row" style="background:var(--surface-2)">
                        <div class="bd"><div class="t">${esc(LOCATION_KIND_LABEL[kind])}</div></div>
                      </div>
                      ${list.length
                        ? list
                            .map(
                              (l) => html`
                                <div class="row">
                                  <div class="bd">
                                    <div class="t"><span style="font-family:var(--mono)">${esc(l.code)}</span> ${esc(l.name)}</div>
                                    <div class="m tiny dim">
                                      ${l.lotCount ?? 0} lot(s)${l.conditions ? ` · ${esc(l.conditions)}` : ''}
                                    </div>
                                  </div>
                                  <button class="btn sm" data-archive-location="${esc(l.id)}">Archive</button>
                                </div>
                              `,
                            )
                            .join('')
                        : '<div class="row"><div class="bd"><div class="m tiny dim">none</div></div></div>'}
                    </div>
                  `;
                })
                .join('')}
            </div>
          `
        : html`<div class="rows"><div class="row"><div class="bd">
            <div class="t">No locations yet</div>
            <div class="m">Add at least a serviceable and a quarantine bin — incoming inspection needs
              somewhere to put what it passes.</div>
          </div></div></div>`}
      <div class="card-f">
        A lot may only sit in a bin that matches its condition. Moving an unserviceable part onto a
        serviceable shelf is refused, not recorded.
      </div>
    </details>
  `;
}

// ── dialogs owned by this screen ────────────────────────────────────────────

function addLocation(o: StoresOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Add a location',
    sub: 'A bin, and what may sit in it',
    primary: 'Add it',
    body: () => html`
      <div class="frow">
        ${field('Code', input('code', '', 'placeholder="A1" style="font-family:var(--mono)" maxlength="24"'))}
        ${field('Kind', select('kind', o.reference.locationKinds
          .map((k) => opt(k, LOCATION_KIND_LABEL[k] ?? k)).join('')))}
      </div>
      ${field('Name', input('name', '', 'placeholder="Main store, rack A" maxlength="120"'))}
      ${field(
        'Storage conditions',
        input('conditions', '', 'placeholder="15–25 °C, out of daylight" maxlength="300"'),
        'From the manufacturer’s instructions, where they state any',
      )}
      ${callout('info', 'i', html`
        The kind is what makes segregation work: the application refuses to put unserviceable stock in a
        serviceable bin, rather than recording that somebody did.
      `)}
    `,
    collect: (root) => {
      const code = val(root, 'code');
      const name = val(root, 'name');
      if (!code || !name) return { error: 'A location needs a code and a name.' };
      return { code, name, kind: val(root, 'kind'), conditions: val(root, 'conditions') || undefined };
    },
    submit: (body) => api.stores.createLocation(body),
    done: (r) => `${r.code} added`,
    onDone: reload,
  });
}

// ── entry point ─────────────────────────────────────────────────────────────

let filter = 'all';
let search = '';
const picked = new Set<string>();

/**
 * @param openLotId a lot to open straight away, from a label QR.
 */
export async function renderStores(host: HTMLElement, openLotId?: string): Promise<void> {
  host.innerHTML = loading('Loading the store');
  try {
    // The whole shelf, once. Searching and filtering are then local, which is
    // what stops a keystroke reloading the screen.
    const o = await api.stores.overview();
    const visible = new Set(o.lots.map((l) => l.id));
    [...picked].forEach((id) => { if (!visible.has(id)) picked.delete(id); });

    const reload = () => void renderStores(host);

    // Only the table is redrawn on a search or a filter change. Repainting the
    // whole page would fold the locations panel, lose the scroll position and
    // take the caret out of the box being typed into.
    const repaintTable = () => {
      const shown = o.lots.filter((l) => matches(l, filter) && searched(l, search));
      const body = host.querySelector('[data-lot-rows]');
      const count = host.querySelector('[data-shown-count]');
      if (!body) return;
      body.innerHTML = shown.length
        ? shown.map((l) => lotRow(l, picked)).join('')
        : `<tr><td colspan="9" class="muted">${
            search ? `Nothing matches “${esc(search)}”.` : 'Nothing on this filter.'
          }</td></tr>`;
      if (count) count.textContent = `${shown.length} of ${o.counts.total} shown`;
      host.querySelectorAll<HTMLElement>('[data-filter]').forEach((b) =>
        b.classList.toggle('on', b.dataset.filter === filter),
      );
      bindRows(host, o, reload);
    };

    host.innerHTML = view(o, filter, picked, search);
    bind(host, o, repaintTable, reload);

    // Straight into the lot, and the URL goes back to the list so a
    // refresh does not reopen the dialog for ever.
    if (openLotId) {
      history.replaceState({}, '', '/stores');
      showLot(openLotId, o, reload);
    }
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load the store.');
  }
}

function bind(host: HTMLElement, o: StoresOverview, repaintTable: () => void, reload: () => void): void {
  host.querySelector('[data-receive]')?.addEventListener('click', () => receiveLot(o, reload));
  host.querySelector('[data-import]')?.addEventListener('click', () => {
    // Its own screen, not a dialog: a delivery is read, checked field by
    // field against the papers it came from, and then received.
    history.pushState({}, '', '/stores/import');
    window.dispatchEvent(new PopStateEvent('popstate'));
  });
  host.querySelector('[data-reserve]')?.addEventListener('click', () => reserveFromAircraft(o, reload));
  host.querySelector('[data-check]')?.addEventListener('click', () => {
    // pushState + a popstate, like the aircraft tabs: importing the router
    // here would make main and the views import each other.
    const go = (path: string) => {
      history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
    if (o.openCheck) return go(`/stores/checks/${o.openCheck.id}`);
    openStockCheck((id) => go(`/stores/checks/${id}`));
  });
  host.querySelector('[data-add-location]')?.addEventListener('click', (e) => {
    // Inside a <summary>: without this the click also toggles the panel shut.
    e.preventDefault();
    e.stopPropagation();
    addLocation(o, reload);
  });

  host.querySelectorAll<HTMLElement>('[data-filter]').forEach((b) =>
    b.addEventListener('click', () => {
      filter = b.dataset.filter!;
      repaintTable();
    }),
  );

  // No debounce and no request: the shelf is already here, so the table can
  // keep up with typing.
  const box = host.querySelector<HTMLInputElement>('[data-search]');
  box?.addEventListener('input', () => {
    search = box.value.trim();
    repaintTable();
  });

  host.querySelector('[data-labels]')?.addEventListener('click', () => {
    if (picked.size) return printLabels([...picked]);
    // Nothing ticked used to leave the button dead, which tells nobody
    // anything. Offer the obvious thing instead: label what is on screen.
    const shown = o.lots.filter((l) => matches(l, filter) && searched(l, search));
    askWhatToLabel(shown.length, () => printLabels(shown.map((l) => l.id)));
  });

  bindRows(host, o, reload);
}

/**
 * Handlers for the rows themselves, rebound whenever the table is redrawn.
 *
 * Separate from `bind` because the header, the filters and the panels survive
 * a search and the rows do not — re-registering everything would stack a
 * listener on the search box for every keystroke.
 */
function bindRows(host: HTMLElement, o: StoresOverview, reload: () => void): void {
  host.querySelectorAll<HTMLInputElement>('[data-pick]').forEach((b) =>
    b.addEventListener('change', () => {
      if (b.checked) picked.add(b.dataset.pick!);
      else picked.delete(b.dataset.pick!);
      const btn = host.querySelector<HTMLButtonElement>('[data-labels]');
      if (btn) btn.textContent = picked.size ? `Print labels (${picked.size})` : 'Print labels';
    }),
  );

  const open = (id: string) => showLot(id, o, reload);
  host.querySelectorAll<HTMLElement>('[data-lot]').forEach((a) =>
    a.addEventListener('click', (e) => {
      e.preventDefault();
      open(a.dataset.lot!);
    }),
  );
  host.querySelectorAll<HTMLElement>('[data-lot-open]').forEach((b) =>
    b.addEventListener('click', () => open(b.dataset.lotOpen!)),
  );
  host.querySelectorAll<HTMLElement>('[data-lot-edit]').forEach((b) =>
    b.addEventListener('click', () => void editLot(b.dataset.lotEdit!, o, reload)),
  );
  host.querySelectorAll<HTMLElement>('[data-inspect]').forEach((b) =>
    b.addEventListener('click', () => {
      const lot = o.lots.find((l) => l.id === b.dataset.inspect);
      if (lot) inspectLot(lot, o, reload);
    }),
  );
  host.querySelectorAll<HTMLElement>('[data-archive-location]').forEach((b) =>
    b.addEventListener('click', async () => {
      try {
        await api.stores.archiveLocation(b.dataset.archiveLocation!);
        toast('Location archived.');
        reload();
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Could not archive it.', 'error');
      }
    }),
  );
}

/** What the Print labels button does when nothing has been ticked. */
function askWhatToLabel(shownCount: number, labelAll: () => void): void {
  if (!shownCount) {
    return toast('There is nothing on this filter to label.', 'error');
  }
  openDialog<boolean>({
    title: 'Nothing is ticked',
    sub: 'Choose what to send to the printer',
    primary: `Label all ${shownCount} shown`,
    body: () => callout('info', 'i', html`
      Tick the lots you want in the left-hand column and press <b>Print labels</b> again, or label
      everything currently listed — ${shownCount} lot${shownCount === 1 ? '' : 's'} on this filter.
    `),
    collect: () => true,
    submit: async (v) => v,
    done: () => '',
    onDone: labelAll,
  });
}
